import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import './App.css';

function HomePage() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const revealPointsRef = useRef([]);
  const animationFrameRef = useRef(null);
  const imageRef = useRef(null);
  const imageLoadedRef = useRef(false);
  const [selectedFeaturedVideo, setSelectedFeaturedVideo] = useState(null);
  
  // Featured work video IDs
  const featuredVideos = ['1166021143', '1167664340', '1166029076', '1167659736'];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let width = window.innerWidth;
    let height = window.innerHeight;

    const setCanvasSize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };

    setCanvasSize();

    const img = new Image();
    img.src = "/our-very-nature.png";
    
    img.onload = () => {
      imageRef.current = img;
      imageLoadedRef.current = true;
    };

    img.onerror = () => {
      console.error("Failed to load image");
    };

    const revealRadius = 150;
    const fadeSpeed = 0.008;
    const maxRevealPoints = 300;

    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
      
      revealPointsRef.current.push({
        x: e.clientX,
        y: e.clientY,
        alpha: 1,
        radius: revealRadius
      });

      if (revealPointsRef.current.length > maxRevealPoints) {
        revealPointsRef.current.shift();
      }
    };

    const animate = () => {
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);

      if (imageLoadedRef.current && imageRef.current) {
        const imgAspect = imageRef.current.width / imageRef.current.height;
        const canvasAspect = width / height;
        
        let drawWidth, drawHeight, offsetX, offsetY;
        
        if (imgAspect > canvasAspect) {
          drawHeight = height;
          drawWidth = height * imgAspect;
          offsetX = (width - drawWidth) / 2;
          offsetY = 0;
        } else {
          drawWidth = width;
          drawHeight = width / imgAspect;
          offsetX = 0;
          offsetY = (height - drawHeight) / 2;
        }

        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = width;
        maskCanvas.height = height;
        const maskCtx = maskCanvas.getContext('2d');

        revealPointsRef.current.forEach(point => {
          if (point.alpha > 0) {
            const layers = 3;
            for (let i = 0; i < layers; i++) {
              const layerRadius = point.radius * point.alpha * (1 + i * 0.3);
              const gradient = maskCtx.createRadialGradient(
                point.x, point.y, 0,
                point.x, point.y, layerRadius
              );
              
              const centerAlpha = point.alpha * (1 - i * 0.3);
              gradient.addColorStop(0, `rgba(255, 255, 255, ${centerAlpha * 0.8})`);
              gradient.addColorStop(0.4, `rgba(255, 255, 255, ${centerAlpha * 0.5})`);
              gradient.addColorStop(0.7, `rgba(255, 255, 255, ${centerAlpha * 0.2})`);
              gradient.addColorStop(1, `rgba(255, 255, 255, 0)`);
              
              maskCtx.fillStyle = gradient;
              maskCtx.fillRect(0, 0, width, height);
            }
          }
        });

        ctx.save();
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
        ctx.drawImage(imageRef.current, offsetX, offsetY, drawWidth, drawHeight);
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(maskCanvas, 0, 0);
        ctx.restore();
      }

      revealPointsRef.current = revealPointsRef.current.filter(point => {
        point.alpha -= fadeSpeed;
        return point.alpha > 0;
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', setCanvasSize);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', setCanvasSize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="App">
      <canvas ref={canvasRef} className="reveal-canvas" />
      
      {/* Navigation */}
      <nav className="navigation" data-testid="main-navigation">
        <button onClick={() => scrollToSection('home')} data-testid="nav-home">Home</button>
        <button onClick={() => scrollToSection('our-very-nature')} data-testid="nav-our-very-nature">Our Nature</button>
        <button onClick={() => scrollToSection('our-team')} data-testid="nav-our-team">Our Team</button>
        <button onClick={() => scrollToSection('find-us')} data-testid="nav-find-us">Find Us</button>
      </nav>

      {/* Content */}
      <div className="content-wrapper">
        {/* Home Section */}
        <section id="home" className="section home-section" data-testid="home-section">
          <img src="/syndicate-logo.png" alt="The Syndicate" className="logo" data-testid="syndicate-logo" />
          
          <div className="featured-work" data-testid="featured-work-section">
            <h2 data-testid="featured-work-heading">Featured Work</h2>
            
            {featuredVideos.map((videoId, index) => (
              <div 
                key={videoId}
                className="video-container featured-video-clickable" 
                data-testid={`video-${index + 1}`}
                onClick={() => setSelectedFeaturedVideo(videoId)}
              >
                <iframe
                  src={`https://player.vimeo.com/video/${videoId}?badge=0&autopause=0&player_id=0&app_id=58479&title=0&byline=0&portrait=0&controls=0&dnt=1&background=1`}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                  title={`Featured Work ${index + 1}`}
                  className="featured-video"
                  style={{ pointerEvents: 'none' }}
                ></iframe>
                <div className="video-overlay"></div>
              </div>
            ))}
          </div>
          
          {/* Featured Work Video Modal */}
          {selectedFeaturedVideo && (
            <div className="showreel-modal" onClick={() => setSelectedFeaturedVideo(null)} data-testid="featured-video-modal">
              <button className="modal-close" onClick={() => setSelectedFeaturedVideo(null)} data-testid="featured-modal-close">✕</button>
              <div className="showreel-modal-content" onClick={(e) => e.stopPropagation()}>
                <iframe
                  src={`https://player.vimeo.com/video/${selectedFeaturedVideo}?badge=0&autopause=0&player_id=0&app_id=58479&title=0&byline=0&portrait=0&controls=1&dnt=1&autoplay=1`}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                  title="Featured Work"
                  className="showreel-modal-video"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          )}
        </section>

        {/* Our Nature Section */}
        <section id="our-very-nature" className="section our-nature-section" data-testid="our-very-nature-section">
          <h2 className="section-title-center" data-testid="our-nature-title">Our Nature</h2>
          <div className="nature-content">
            <p>We understand the alchemy and artistry of post-production, where the ordinary transcends into the extraordinary.</p>
            <p>We understand that every project has a unique narrative, and our job is to amplify its voice.</p>
            <p>We are dreamers and storytellers, united by our love for the craft.</p>
            <p>We live and breathe the visual arts.</p>
            <p className="services" data-testid="services-list">OFFLINE | GRADE | ONLINE | AUDIO | AI GENERATION & EDITING | VFX & ANIMATION | ON-SET EDITING & SUPERVISION</p>
          </div>
        </section>

        {/* Our Team Section */}
        <section id="our-team" className="section team-section" data-testid="our-team-section">
          <h2 className="section-title-left" data-testid="team-section-title">Our Team</h2>
          <div className="team-grid">
            {/* 1. Gugu Radebe - No showreel */}
            <div 
              className="team-card polaroid" 
              data-testid="team-member-1"
              onMouseEnter={(e) => {
                const video = e.currentTarget.querySelector('video');
                if (video) video.play();
              }}
              onMouseLeave={(e) => {
                const video = e.currentTarget.querySelector('video');
                if (video) {
                  video.pause();
                  video.currentTime = 0;
                }
              }}
            >
              <div className="polaroid-photo">
                <video 
                  className="team-video" 
                  loop 
                  muted 
                  playsInline
                  data-testid="team-video-1"
                >
                  <source src="/gugu-radebe.mp4" type="video/mp4" />
                </video>
              </div>
              <div className="polaroid-caption">
                <h3 className="team-role" data-testid="team-role-1">Producer</h3>
                <h4 className="team-name" data-testid="team-name-1">Gugu Radebe</h4>
              </div>
            </div>
            
            {/* 2. Nic Goodwin */}
            <div 
              className="team-card polaroid" 
              data-testid="team-member-2"
              onMouseEnter={(e) => {
                const video = e.currentTarget.querySelector('video');
                if (video) video.play();
              }}
              onMouseLeave={(e) => {
                const video = e.currentTarget.querySelector('video');
                if (video) {
                  video.pause();
                  video.currentTime = 0;
                }
              }}
            >
              <div className="polaroid-photo">
                <video 
                  className="team-video" 
                  loop 
                  muted 
                  playsInline
                  data-testid="team-video-2"
                >
                  <source src="/nic-goodwin.mp4" type="video/mp4" />
                </video>
              </div>
              <div className="polaroid-caption">
                <h3 className="team-role" data-testid="team-role-2">Editor</h3>
                <h4 className="team-name" data-testid="team-name-2">Nic Goodwin</h4>
                <Link to="/showreel/nic-goodwin" className="showreel-link" data-testid="showreel-link-2">Showreel</Link>
                <div className="team-social" data-testid="team-social-2">
                  <a href="#" aria-label="Facebook"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/></svg></a>
                  <a href="#" aria-label="Twitter"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg></a>
                  <a href="#" aria-label="LinkedIn"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z"/></svg></a>
                </div>
              </div>
            </div>
            
            {/* 3. Ndivhu Mushanganyisi */}
            <div 
              className="team-card polaroid" 
              data-testid="team-member-3"
              onMouseEnter={(e) => {
                const video = e.currentTarget.querySelector('video');
                if (video) video.play();
              }}
              onMouseLeave={(e) => {
                const video = e.currentTarget.querySelector('video');
                if (video) {
                  video.pause();
                  video.currentTime = 0;
                }
              }}
            >
              <div className="polaroid-photo">
                <video 
                  className="team-video" 
                  loop 
                  muted 
                  playsInline
                  data-testid="team-video-3"
                >
                  <source src="/ndivhu-mushanganyisi.mp4" type="video/mp4" />
                </video>
              </div>
              <div className="polaroid-caption">
                <h3 className="team-role" data-testid="team-role-3">Editor</h3>
                <h4 className="team-name" data-testid="team-name-3">Ndivhu Mushanganyisi</h4>
                <Link to="/showreel/ndivhu-mushanganyisi" className="showreel-link" data-testid="showreel-link-3">Showreel</Link>
                <div className="team-social" data-testid="team-social-3">
                  <a href="#" aria-label="Facebook"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/></svg></a>
                  <a href="#" aria-label="Twitter"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg></a>
                  <a href="#" aria-label="LinkedIn"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z"/></svg></a>
                </div>
              </div>
            </div>
            
            {/* 4. James O'Sullivan */}
            <div 
              className="team-card polaroid" 
              data-testid="team-member-4"
              onMouseEnter={(e) => {
                const video = e.currentTarget.querySelector('video');
                if (video) video.play();
              }}
              onMouseLeave={(e) => {
                const video = e.currentTarget.querySelector('video');
                if (video) {
                  video.pause();
                  video.currentTime = 0;
                }
              }}
            >
              <div className="polaroid-photo">
                <video 
                  className="team-video" 
                  loop 
                  muted 
                  playsInline
                  data-testid="team-video-4"
                >
                  <source src="/james-osullivan.mp4" type="video/mp4" />
                </video>
              </div>
              <div className="polaroid-caption">
                <h3 className="team-role" data-testid="team-role-4">Editor</h3>
                <h4 className="team-name" data-testid="team-name-4">James O' Sullivan</h4>
                <Link to="/showreel/james-osullivan" className="showreel-link" data-testid="showreel-link-4">Showreel</Link>
                <div className="team-social" data-testid="team-social-4">
                  <a href="#" aria-label="Facebook"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/></svg></a>
                  <a href="#" aria-label="Twitter"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg></a>
                  <a href="#" aria-label="LinkedIn"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z"/></svg></a>
                </div>
              </div>
            </div>
            
            {/* 5. Shelby Ncube */}
            <div className="team-card polaroid" data-testid="team-member-5">
              <div className="polaroid-photo">
                <div className="team-image-placeholder" data-testid="team-image-5">
                  <div className="placeholder-icon">👤</div>
                </div>
              </div>
              <div className="polaroid-caption">
                <h3 className="team-role" data-testid="team-role-5">Freelance Editor</h3>
                <h4 className="team-name" data-testid="team-name-5">Shelby Ncube</h4>
                <Link to="/showreel/shelby-ncube" className="showreel-link" data-testid="showreel-link-5">Showreel</Link>
                <div className="team-social" data-testid="team-social-5">
                  <a href="#" aria-label="Facebook"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/></svg></a>
                  <a href="#" aria-label="Twitter"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg></a>
                  <a href="#" aria-label="LinkedIn"><svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z"/></svg></a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Find Us Section */}
        <section id="find-us" className="section find-us-section" data-testid="find-us-section">
          <h2 className="section-title-left" data-testid="find-us-title">Find Us</h2>
          <div className="contact-grid">
            <div className="contact-card" data-testid="contact-gugu">
              <h3>Gugu Radebe</h3>
              <p className="contact-role">Producer</p>
              <p className="contact-detail"><a href="tel:0712995072" data-testid="gugu-phone">071 299 5072</a></p>
              <p className="contact-detail"><a href="mailto:gugu@thesyndicate.media" data-testid="gugu-email">gugu@thesyndicate.media</a></p>
            </div>
            
            <div className="contact-card" data-testid="contact-nic">
              <h3>Nic Goodwin</h3>
              <p className="contact-role">Editor</p>
              <p className="contact-detail"><a href="tel:0749011362" data-testid="nic-phone">074 901 1362</a></p>
              <p className="contact-detail"><a href="mailto:nic@thesyndicate.media" data-testid="nic-email">nic@thesyndicate.media</a></p>
            </div>
          </div>
          
          <div className="address-map" data-testid="address-map-section">
            <div className="address-info">
              <h3>Address</h3>
              <p data-testid="physical-address">39 4th Avenue, Parkhurst, Randburg</p>
            </div>
            <a 
              href="https://www.google.com/maps/place/39+4th+Ave,+Parkhurst,+Randburg,+2193,+South+Africa/@-26.1384,28.0194,17z" 
              target="_blank" 
              rel="noopener noreferrer"
              className="map-link"
              data-testid="map-link"
            >
              <div className="map-embed">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3584.5!2d28.0194!3d-26.1384!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e950c1234567890%3A0x1234567890abcdef!2s39%204th%20Ave%2C%20Parkhurst%2C%20Randburg%2C%202193%2C%20South%20Africa!5e0!3m2!1sen!2sza!4v1234567890"
                  width="100%"
                  height="300"
                  style={{ border: 0, borderRadius: '8px', pointerEvents: 'none' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="The Syndicate Location"
                ></iframe>
                <div className="map-overlay-click">
                  <span>📍 Click to open in Google Maps</span>
                </div>
              </div>
            </a>
          </div>
          
          <div className="social-links" data-testid="social-links-section">
            <a 
              href="https://www.instagram.com/thesyndicate_post/" 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="instagram-link"
            >
              <span className="social-icon-large">📷</span> Instagram
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}

// Showreel Pages
function ShowreelPage({ name, videos }) {
  const [selectedVideo, setSelectedVideo] = useState(null);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <div className="showreel-page">
      <nav className="showreel-nav">
        <Link to="/" className="back-button">← Back to Home</Link>
      </nav>
      <div className="showreel-content">
        <h1>{name}</h1>
        <div className="showreel-videos">
          {videos.map((videoId, index) => (
            <div 
              key={index} 
              className="showreel-video-container"
              onClick={() => setSelectedVideo(videoId)}
              data-testid={`showreel-video-${index}`}
            >
              <iframe
                src={`https://player.vimeo.com/video/${videoId}?badge=0&autopause=0&player_id=0&app_id=58479&title=0&byline=0&portrait=0&controls=0&dnt=1&background=1`}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                title={`${name} Showreel ${index + 1}`}
                className="showreel-video"
                style={{ pointerEvents: 'none' }}
              ></iframe>
              <div className="video-overlay"></div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Video Modal */}
      {selectedVideo && (
        <div className="showreel-modal" onClick={() => setSelectedVideo(null)}>
          <button className="modal-close" onClick={() => setSelectedVideo(null)}>✕</button>
          <div className="showreel-modal-content" onClick={(e) => e.stopPropagation()}>
            <iframe
              src={`https://player.vimeo.com/video/${selectedVideo}?badge=0&autopause=0&player_id=0&app_id=58479&title=0&byline=0&portrait=0&controls=1&dnt=1&autoplay=1`}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
              title={`${name} Showreel`}
              className="showreel-modal-video"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
}

// Main App with Router
function App() {
  // Nic's videos
  const nicVideos = ['1163880569', '1163881193', '1163880762', '1163880548', '1163880605', '1163881073', '1163880626', '1163880937', '1163881033', '1163881110'];
  
  // James's videos
  const jamesVideos = ['1166021143', '1166019099', '1166022986', '1166025450', '1166026474', '1166027937', '1166029076', '1166028594'];
  
  // Ndivhu's videos
  const ndivhuVideos = ['1166116011', '1166115956', '1166115728', '1166111883', '1166112687', '1166115804', '1166115878', '1166117276', '1166112789'];
  
  // Shelby's videos
  const shelbyVideos = ['1166111210', '1166105248', '1166110008', '1166104596', '1166108015', '1166108810', '1166108165'];
  
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/showreel/nic-goodwin" element={<ShowreelPage name="Nic Goodwin" videos={nicVideos} />} />
        <Route path="/showreel/james-osullivan" element={<ShowreelPage name="James O' Sullivan" videos={jamesVideos} />} />
        <Route path="/showreel/ndivhu-mushanganyisi" element={<ShowreelPage name="Ndivhu Mushanganyisi" videos={ndivhuVideos} />} />
        <Route path="/showreel/shelby-ncube" element={<ShowreelPage name="Shelby Ncube" videos={shelbyVideos} />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;